import type { Block, DecideReason, FormBlock, KvBlock, NoteBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import { CANDIDATES_PATH, readCandidate, type CandidateStanding } from './candidates'
import { callerBeside, submitWrite, writeRefusal } from './writes'
import type { Caller } from './handles'
import { readAtStamp, type ScreenData } from './provenance'

/**
 * The two decisions on a catalog candidate: promote it, or dismiss it.
 *
 * # This is law 17's implementation, and the shape IS the enforcement
 *
 * Promotion is *"the ONLY path from user feedback into the catalog"*
 * (`internal/adminops/promote.go`), and the rule it exists to hold is that the
 * crowd raises a candidate and an OPERATOR decides it. Three things here follow
 * from that rather than from taste:
 *
 * **The platform field starts empty and cannot be pre-filled.** The queue's own
 * field is `proposed_platform_id` — renamed on purpose, with a backend test
 * pinning the rename, because *"a field named as though it were the answer is
 * how somebody promotes by pressing enter"*. The proposal is rendered beside
 * the control as evidence, never into it. The backend refuses a body without a
 * platform and says why in words rather than in a code; this console makes the
 * refusable request unreachable instead.
 *
 * **Neither sample is offered.** `sample_iban` is a keyed pseudonym of a third
 * party's account (#459) and `sample_name` is a third party's name (#533).
 * `PromoteCandidate` does not read either column, deliberately — *"so that
 * stays true rather than depending on the branch below staying deleted"* — and
 * there is correspondingly no field here that could carry either into an entry.
 * An operator who has verified a platform's payout IBAN adds it through the
 * ordinary versioned catalog edit, where it is their stated fact rather than
 * one fan's payment quietly becoming everybody's matching rule.
 *
 * **There is no volume threshold anywhere.** Not a constant, not a chip, not a
 * sentence saying a candidate is ready. The domain refuses to hold one for the
 * same reason: *"a threshold constant is what a later change turns into an
 * automatic trigger."* A hundred people saying something is Patreon is a strong
 * reason for an operator to look and no reason at all for the system to act —
 * otherwise the cost of manufacturing a hundred votes is the cost of poisoning
 * recognition platform-wide.
 *
 * # Invariant 11b moves onto the control
 *
 * The queue already says, standing and always, that this read cannot tell an
 * orchestrator from a platform. That was a caveat under a table nobody could
 * act on. With a live promote control it is a caveat on an act, so it is
 * repeated at the point of the act: promoting a route rather than a payer
 * attributes one customer's income to everybody else behind the same route, and
 * no field on this screen can tell them apart.
 */

/** `catalog.candidate.decide` — OPS, and separate from `catalog.entry.write`. */
const PERM_DECIDE = 'catalog.candidate.decide'

/**
 * The role, as an operator would ask for it.
 *
 * Ops, checked against `staff.go`'s grant table rather than inferred from the
 * section. It is deliberately NOT `catalog.entry.write`'s permission even
 * though a promotion creates an entry: *"deciding a queue item and authoring a
 * catalog rule are different acts"*, and keeping them apart is what lets the
 * queue be delegated or withdrawn without touching direct edits.
 */
const ROLE = 'Ops'

export function promoteCandidatePath(id: number): string {
  return `${CANDIDATES_PATH}/${String(id)}/promote`
}

export function dismissCandidatePath(id: number): string {
  return `${CANDIDATES_PATH}/${String(id)}/dismiss`
}

/**
 * Law 21: a reason code from a closed list, never free text.
 *
 * This console's own list, and said to be — there is no reason-code registry on
 * the admin surface to read, and `cf-reasons` says on screen that there never
 * will be one. Split by act, because "the payer is a route rather than a
 * platform" is a reason to dismiss and never a reason to promote, and the audit
 * row is read years later by somebody who has only the code.
 */
const PROMOTE_REASONS: readonly DecideReason[] = [
  ['payer_confirmed_by_operator', 'Checked the payer against the platform’s own records', ''],
  ['consistent_across_customers', 'The same payer maps to this platform for unrelated customers', ''],
  ['platform_documented_payer', 'The platform publishes this as its paying entity', ''],
  ['recovers_known_gap', 'This closes a recognition gap already being chased', ''],
]

const DISMISS_REASONS: readonly DecideReason[] = [
  ['orchestrator_not_platform', 'The payer routes for many platforms — no global answer exists', ''],
  ['payer_not_identifiable', 'The evidence does not establish who this payer is', ''],
  ['duplicate_of_live_entry', 'The catalog already has an entry for this payer', ''],
  ['observations_from_one_customer', 'The count is one customer repeated, not a consensus', ''],
  ['feedback_appears_manufactured', 'The pattern suggests the evidence was gamed', ''],
]

/**
 * Promoting. The operator names the platform; that statement is the whole act.
 *
 * `confirm` is the candidate's own id. The difference between promoting 918 and
 * 919 is invisible to a dialog that asks only whether you are sure, and the two
 * are different payers becoming different catalog rules for everybody.
 */
export function promoteCandidateForm(
  api: ApiClient, caller: Caller, c: CandidateStanding,
): FormBlock {
  const blocked = writeRefusal(caller, PERM_DECIDE, ROLE, 'Promoting a catalog candidate')
  return {
    t: 'form',
    title: `Promote candidate ${String(c.id)}`,
    lead: `Creates a catalog entry that will tag this payer for EVERY customer, not only the ones who reported it. ${c.payer} has been observed ${c.observations} — a count is evidence that somebody should look, and never a mandate to promote.`,
    fields: [
      {
        name: 'entry_key', label: 'Entry key', kind: 'text', required: true, mono: true,
        note: 'The key the new catalog entry is filed under. Permanent once written, and the thing every later version of this rule hangs off.',
      },
      {
        // EMPTY, and there is no code path that fills it. The crowd's proposal
        // is on the evidence panel above and is not this field's value.
        name: 'platform_id', label: 'Platform', kind: 'text', required: true, mono: true,
        note: `The platform YOU are stating this payer pays for. The queue's suggestion is ${c.proposedPlatform === '' ? 'absent from this row' : `"${c.proposedPlatform}"`} — it is shown above as evidence and is deliberately not filled in here, because a pre-filled answer is one somebody accepts by pressing enter.`,
      },
    ],
    reasons: PROMOTE_REASONS,
    reasonNote: 'What you checked, not what the queue suggested. Read by whoever inherits this rule and by anybody asking why a customer’s income was attributed the way it was.',
    auditLine: `admin.catalog.candidate.promote · actor · catalog_candidate:${String(c.id)} · platform_id · entry_key · reason`,
    cta: 'Promote to the catalog',
    ctaNote: 'The entry is global from the moment it is written: it tags this payer for every customer, including those who never reported anything. Neither the sample payer name nor the sample account reference is copied into it — an unverified account reference is worse than no entry, because it is the strongest matching signal there is and a wrong one produces a confident WRONG tag rather than a missed one.',
    blocked,
    confirm: String(c.id),
    ...(blocked === '' ? { submit: promoteSubmit(api, c) } : {}),
  }
}

function promoteSubmit(api: ApiClient, c: CandidateStanding) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(promoteCandidatePath(c.id), {
      entry_key: v['entry_key'] ?? '',
      // Sent exactly as typed and never substituted from the proposal. A
      // fallback here would be the default the backend refuses to have, moved
      // one layer out to where nothing refuses it.
      platform_id: v['platform_id'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The candidate was promoted',
    (answer) => {
      const key = stringField(answer, 'entry_key')
      return [
        ['entry', key === '' ? '— not returned' : key, 'now tagging this payer for every customer'],
        ['candidate', String(c.id), 'closed — it leaves the queue and cannot be promoted twice'],
      ]
    },
  )
}

/**
 * Dismissing. Closes the candidate and writes nothing to the catalog.
 *
 * No confirmation typing on this one, and the asymmetry is deliberate: promoting
 * creates a rule that tags a payer for everybody, and dismissing leaves the
 * world exactly as it was. Requiring the same ceremony for both would teach an
 * operator to type ids without reading them, on the act where reading them
 * matters.
 */
export function dismissCandidateForm(
  api: ApiClient, caller: Caller, c: CandidateStanding,
): FormBlock {
  const blocked = writeRefusal(caller, PERM_DECIDE, ROLE, 'Dismissing a catalog candidate')
  return {
    t: 'form',
    title: `Dismiss candidate ${String(c.id)}`,
    lead: `Closes ${c.payer} without a catalog entry. Nothing is written to the catalog and no customer's history changes; the payer simply stays unrecognised until somebody raises it again.`,
    fields: [],
    reasons: DISMISS_REASONS,
    reasonNote: 'The code is what somebody sees if this payer comes back. "It routes for many platforms" and "we could not identify it" send the next operator to different places.',
    auditLine: `admin.catalog.candidate.dismiss · actor · catalog_candidate:${String(c.id)} · reason`,
    cta: 'Dismiss this candidate',
    ctaNote: 'The candidate leaves the queue. The feedback behind it is not deleted — it stays in the evidence stream, and a fresh wave of observations raises a new candidate rather than reopening this one.',
    blocked,
    ...(blocked === '' ? { submit: dismissSubmit(api, c) } : {}),
  }
}

function dismissSubmit(api: ApiClient, c: CandidateStanding) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(dismissCandidatePath(c.id), { reason: v['reason'] ?? '' }),
    'The candidate was dismissed',
    (answer) => {
      const state = stringField(answer, 'state')
      return [
        ['candidate', String(c.id), state === '' ? 'closed' : `now ${state}`],
        ['the catalog', 'unchanged', 'nothing was written and no customer’s history moved'],
      ]
    },
  )
}

function stringField(answer: unknown, key: string): string {
  if (typeof answer !== 'object' || answer === null) return ''
  const value = (answer as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : ''
}

/**
 * The evidence an operator is deciding from, as its own panel.
 *
 * Separate from the controls, and above them, because it is the half of the
 * screen the design draws and this read CAN fill. What it cannot fill stays
 * named as absent on the queue's own note rather than being quietly omitted
 * here — the confidence tier, the per-signal evidence and the sample
 * transaction ids have no field behind them, and inventing them beside a real
 * candidate id is the worst thing this file could do.
 *
 * The proposal is a line on this panel and is labelled as the crowd's. That is
 * the whole of where it appears.
 */
function evidencePanel(c: CandidateStanding): KvBlock {
  return {
    t: 'kv',
    title: `Candidate ${String(c.id)} — what the queue knows`,
    src: 'ledger',
    items: [
      ['Payer as observed', c.payer, '', 'n', 'the sample the aggregate kept. Neither half of it is copied into a catalog entry.', 'm'],
      ['Candidate key', c.candidateKey === '' ? '— not stated' : c.candidateKey, '', 'n', '', 'm'],
      ['Observations', c.observations, '', 'n', 'observations, not people: one customer paid forty-one times raises a count of 41 with a consensus of one.', ''],
      ['Currency', c.currency === '' ? '— none recorded' : c.currency, '', c.currency === '' ? 'a' : 'n', c.currency === '' ? 'the observations did not agree on one, so this candidate’s scope is unknown.' : '', 'm'],
      [
        'Suggested by the crowd',
        c.proposedPlatform === '' ? '— none proposed' : c.proposedPlatform,
        'suggestion',
        'a',
        'Aggregated from customer feedback, and not an answer. It is shown here and is deliberately not filled into the field below — state the platform yourself.',
        'm',
      ],
    ],
  }
}

/** Invariant 11b, at the point of the act rather than under the table. */
const ORCHESTRATOR_WARNING: NoteBlock = {
  t: 'note',
  tone: 'r',
  kicker: 'Invariant 11b',
  title: 'Nothing here can tell you whether this payer is a route',
  body: 'An orchestrator — MassPay, Payoneer — is a payer like any other on this queue, and "who pays you through MassPay?" has one answer per customer and no global one. This read carries no flag separating the two, so promoting a route attributes one customer’s income to everyone else behind it. If there is any chance this payer routes for several platforms, dismiss it as an orchestrator rather than promote it: a per-user mapping is per-customer state and no path anywhere may promote one.',
  ref: 'FR-3.3b · a promotion is global from the moment it is written',
}

/**
 * What a candidate row opens into: its evidence, and the two decisions.
 *
 * # Why this re-reads the queue instead of trusting the row
 *
 * There is no per-candidate GET on this backend, so the queue read is the only
 * source — and repeating it is the better shape rather than a workaround. The
 * endpoint selects `promoted_at IS NULL AND dismissed_at IS NULL`, so a
 * candidate somebody else decided while this page was open is simply ABSENT
 * from the re-read. That absence is a live answer at the moment of the
 * decision, and it is why a promotion composed here is composed against a
 * candidate that was open a second ago rather than one that was open when the
 * page was loaded.
 *
 * The two reads are the queue and the caller's own authority, and the second
 * cannot fail the first: an unread `/me` leaves the evidence fully readable and
 * both controls dead with the reason said out loud.
 */
export async function openCandidate(api: ApiClient, key: string): Promise<ScreenData> {
  const who = callerBeside(api)
  const readAt = new Date()
  let payload: unknown
  try {
    payload = await api.get<unknown>(CANDIDATES_PATH)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const ref = error instanceof ApiError ? error.decisionRef : null
    return single({
      t: 'note',
      tone: 'r',
      kicker: 'Candidate',
      title: `Candidate ${key} could not be re-read`,
      body: 'The queue is re-read when a candidate is opened, so that a decision is composed against what is open now rather than against what was open when the page loaded. That read did not complete, so no control is offered — a promotion is global and permanent, and composing one against an unread candidate is not something to offer.',
      ref: ref === null ? `Asked for: candidate ${key}` : `decision_ref ${ref}`,
    }, 'The queue could not be re-read, so no decision is offered against a candidate nobody has confirmed is still open.')
  }

  const found = findCandidate(payload, key)
  if (found === null) {
    return single({
      t: 'note',
      tone: 'a',
      kicker: 'Candidate',
      title: `Candidate ${key} is no longer on the queue`,
      body: 'The queue returns only candidates nobody has promoted or dismissed, so a candidate missing from a fresh read has been decided by somebody else since this page was loaded — or was never on it. Those two are the same answer from here, and neither is a candidate you can decide: the table above is stale, and re-opening the screen is what makes it agree.',
      ref: 'read · promoted_at IS NULL AND dismissed_at IS NULL',
    }, 'This candidate is not on a fresh read of the queue, so it has been decided by somebody else or was never open.')
  }

  const caller = await who
  return {
    blocks: [
      evidencePanel(found),
      ORCHESTRATOR_WARNING,
      promoteCandidateForm(api, caller, found),
      dismissCandidateForm(api, caller, found),
    ],
    provenance: {
      kind: 'partial',
      source: `postgres · catalog_candidates re-read for candidate ${String(found.id)} (GET /v1/admin${CANDIDATES_PATH})`,
      authority: 'operational',
      // Stamped, and not left blank. The tooltip renders "Read from … at
      // {readAt}", so an empty stamp puts "at ." on a panel whose whole point
      // is that it was re-read a moment ago — and freshness is the argument for
      // re-reading at all. The console's receive time, like the queue's own,
      // because this endpoint sends no server read time either.
      readAt: `${readAtStamp(readAt)} (console receive time — the endpoint sends no server read time)`,
      missing: [
        'match confidence and per-signal evidence (no endpoint answers for either)',
        'whether this payer is an orchestrator rather than a platform (invariant 11b)',
      ],
    },
  }
}

function single(note: NoteBlock, reason: string): ScreenData {
  const blocks: Block[] = [note]
  return {
    blocks,
    provenance: {
      kind: 'unread',
      source: `postgres · catalog_candidates (GET /v1/admin${CANDIDATES_PATH})`,
      reason,
    },
  }
}

/**
 * The opened candidate, or nothing.
 *
 * Matched on the id the ROW carried, and a row whose id could not be stated
 * exactly never becomes a row key — so there is no path here from an id wrong
 * in its last digits to a promotion against a different candidate. The same
 * `readCandidate` the queue uses parses the row, so the control and the table
 * cannot disagree about what a candidate is.
 */
function findCandidate(payload: unknown, key: string): CandidateStanding | null {
  if (typeof payload !== 'object' || payload === null) return null
  const list = (payload as { candidates?: unknown }).candidates
  if (!Array.isArray(list)) return null
  for (const entry of list) {
    const c = readCandidate(entry)
    if (c !== null && String(c.id) === key) return c
  }
  return null
}
